import { Checkbox } from '@dc-shared-web/components/shadcn/checkbox'
import { UseFormReturn } from 'react-hook-form'

interface ChecklistOption {
  label: string
  fieldName: string
  className: string
  show: boolean
  id: string
  optionGroup: 1 | 2 | 3
  questionOptions: {
    label: string
    fieldName: string
  }[]
  additionalOptions: {
    label: string
    fieldName: string
    show?: boolean
  }[]
}

interface PlaybookOption {
  label: string
  fieldName: string
  className: string
  show: boolean
  id: string
  additionalOptions: never[]
}

interface ExportOptionsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>
  checklistOptions?: ChecklistOption[]
  playbookOptions?: PlaybookOption[]
}

/**
 * Component for rendering export options (checklists and playbooks)
 */
export const ExportOptions: React.FC<ExportOptionsProps> = ({
  form,
  checklistOptions,
  playbookOptions,
}) => {
  return (
    <div className='tw-bg-subtle tw-mt-4 tw-p-4 tw-rounded'>
      <h3
        className='tw-uppercase tw-text-neutral-500 tw-text-sm tw-mb-3'
        id='export-options-heading'
      >
        Export Options
      </h3>
      <div
        className='tw-flex tw-flex-col tw-gap-3'
        role='group'
        aria-labelledby='export-options-heading'
      >
        {/* Playbook options */}
        {playbookOptions &&
          playbookOptions.length > 0 &&
          playbookOptions.map((option, index) => (
            <div key={`playbook-item-${index}`}>
              <div className='tw-flex tw-items-center tw-gap-2'>
                <Checkbox
                  id={`playbook-${index}`}
                  checked={
                    form.watch(`selectedPlaybooks.${index}.include`) || false
                  }
                  onCheckedChange={(checked) => {
                    const currentPlaybooks =
                      form.getValues('selectedPlaybooks') || []
                    if (currentPlaybooks[index]) {
                      currentPlaybooks[index].include = !!checked
                    } else {
                      currentPlaybooks[index] = {
                        id: option.id,
                        include: !!checked,
                      }
                    }
                    form.setValue('selectedPlaybooks', currentPlaybooks)
                  }}
                  data-testid={`checkbox-playbook-${index}`}
                  aria-label={`Include ${option.label}`}
                />
                <label
                  htmlFor={`playbook-${index}`}
                  className='tw-text-sm tw-cursor-pointer'
                >
                  {option.label}
                </label>
              </div>

              {/* Add separator line after each playbook item except the last one */}
              {index !== playbookOptions.length - 1 && (
                <hr
                  className='tw-border-t tw-border-neutral-200 tw-m-0 tw-mt-3'
                  aria-hidden='true'
                />
              )}

              {/* Add separator line after playbook options if checklist options exist */}
              {index === playbookOptions.length - 1 &&
                checklistOptions &&
                checklistOptions.length > 0 && (
                  <hr
                    className='tw-border-t tw-border-neutral-200 tw-m-0 tw-mt-3'
                    aria-hidden='true'
                  />
                )}
            </div>
          ))}

        {/* Checklist options */}
        {checklistOptions &&
          checklistOptions.length > 0 &&
          checklistOptions.map((option, index) => (
            <div key={`checklist-item-${index}`}>
              <div className='tw-flex tw-items-center tw-gap-2'>
                <Checkbox
                  id={`checklist-${index}`}
                  checked={
                    form.watch(`selectedChecklists.${index}.include`) || false
                  }
                  onCheckedChange={(checked) => {
                    const currentChecklists =
                      form.getValues('selectedChecklists') || []
                    if (currentChecklists[index]) {
                      currentChecklists[index].include = !!checked
                    } else {
                      currentChecklists[index] = {
                        id: option.id,
                        include: !!checked,
                      }
                    }
                    form.setValue('selectedChecklists', currentChecklists)
                  }}
                  data-testid={`checkbox-checklist-${index}`}
                  aria-label={`Include ${option.label}`}
                />
                <label
                  htmlFor={`checklist-${index}`}
                  className='tw-text-sm tw-cursor-pointer'
                >
                  {option.label}
                </label>
              </div>

              {/* Add separator line after each item except the last one */}
              {index !== checklistOptions.length - 1 && (
                <hr
                  className='tw-border-t tw-border-neutral-200 tw-m-0 tw-mt-3'
                  aria-hidden='true'
                />
              )}
            </div>
          ))}
      </div>
    </div>
  )
}

export default ExportOptions
